<script lang="ts">
  import { onMount } from 'svelte'

  import type { ExternalEvalBenchmark } from './external-evals-catalog'

  import Icon from '$lib/components/Icon.svelte'

  interface Props {
    benchmark: ExternalEvalBenchmark
    selectedTaskIds: string[]
    onApply: (taskIds: string[]) => void
    onClose: () => void
  }

  let { benchmark, selectedTaskIds, onApply, onClose }: Props = $props()

  let pickerElement = $state<HTMLDivElement>()
  let draftTaskIds = $state<string[]>([])
  let initialized = $state(false)

  $effect(() => {
    if (initialized) {
      return
    }
    const benchmarkTaskIds = new Set(benchmark.tasks.map((task) => task.id))
    draftTaskIds = selectedTaskIds.filter((taskId) => benchmarkTaskIds.has(taskId))
    initialized = true
  })

  onMount(() => {
    const previouslyFocused = document.activeElement
    pickerElement?.focus()
    // Returning focus to the `Choose tasks` trigger keeps a keyboard user's place in the
    // launch form instead of dropping them back at the top of the page.
    return () => {
      if (previouslyFocused instanceof HTMLElement && previouslyFocused.isConnected) {
        previouslyFocused.focus()
      }
    }
  })

  const toggleAllTasks = () => {
    const benchmarkTaskIds = benchmark.tasks.map((task) => task.id)
    draftTaskIds = draftTaskIds.length === benchmarkTaskIds.length ? [] : benchmarkTaskIds
  }

  const getFocusableElements = () =>
    Array.from(
      pickerElement?.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])',
      ) ?? [],
    )

  // The backdrop hides the launch form without removing it from the tab order, so Tab has to
  // be cycled inside the dialog. `pickerElement` holds initial focus but is not itself
  // tabbable, which makes it the effective first stop when tabbing backwards.
  const trapFocus = (event: KeyboardEvent) => {
    const focusable = getFocusableElements()
    if (focusable.length === 0) {
      event.preventDefault()
      return
    }
    const first = focusable[0]
    const last = focusable[focusable.length - 1]
    const active = document.activeElement
    if (event.shiftKey) {
      if (active === first || active === pickerElement) {
        event.preventDefault()
        last.focus()
      }
      return
    }
    if (active === last) {
      event.preventDefault()
      first.focus()
    }
  }

  const onDialogKeydown = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      onClose()
      return
    }
    if (event.key === 'Tab') {
      trapFocus(event)
    }
  }
</script>

<div class="fixed inset-0 z-[1000] flex items-center justify-center p-4">
  <div class="absolute inset-0 bg-black/60" aria-hidden="true"></div>
  <div
    bind:this={pickerElement}
    class="card relative z-10 flex max-h-[80vh] w-full max-w-xl flex-col overflow-hidden p-5 shadow-2xl"
    role="dialog"
    aria-modal="true"
    aria-labelledby="task-picker-title"
    tabindex="-1"
    onkeydown={onDialogKeydown}
  >
    <header class="flex items-start justify-between gap-4">
      <div>
        <h2 id="task-picker-title" class="text-lg font-semibold">{benchmark.title} tasks</h2>
        <p class="mt-1 text-sm text-surface-500 dark:text-surface-300">
          Choose which tasks to include in this evaluation.
        </p>
      </div>
      <button
        type="button"
        class="text-surface-600 hover:text-primary-600 dark:text-surface-400 dark:hover:text-primary-300"
        aria-label="Close task picker"
        onclick={onClose}
      >
        <Icon icon="iconoir:xmark-circle" class="h-5 w-5" />
      </button>
    </header>

    <label
      class="mt-4 flex cursor-pointer items-center gap-3 border-b border-surface-400 pb-3 dark:border-surface-600"
    >
      <input
        type="checkbox"
        checked={draftTaskIds.length === benchmark.tasks.length}
        indeterminate={draftTaskIds.length > 0 && draftTaskIds.length < benchmark.tasks.length}
        aria-label={`Select all ${benchmark.title} tasks in picker`}
        onchange={toggleAllTasks}
      />
      <span class="font-medium">Select all tasks</span>
      <span class="ml-auto text-sm text-surface-500 dark:text-surface-300">
        {draftTaskIds.length} of {benchmark.tasks.length}
      </span>
    </label>

    <div class="min-h-0 flex-1 space-y-2 overflow-y-auto py-3">
      {#each benchmark.tasks as task (task.id)}
        <label
          class="flex cursor-pointer items-start gap-3 rounded-md border border-surface-400 p-3 hover:border-primary-500 dark:border-surface-600"
        >
          <input
            class="mt-1"
            type="checkbox"
            bind:group={draftTaskIds}
            value={task.id}
            aria-label={`Select ${task.name}`}
          />
          <span class="min-w-0 flex-1 font-medium">{task.name}</span>
          <span class="shrink-0 text-xs text-surface-500 dark:text-surface-300">
            {task.sampleCount?.toLocaleString() ?? 'Unknown'} samples
          </span>
        </label>
      {/each}
    </div>

    <footer class="flex justify-end gap-2 border-t border-surface-400 pt-4 dark:border-surface-600">
      <button type="button" class="variant-ghost-surface btn" onclick={onClose}>Cancel</button>
      <button
        type="button"
        class="variant-filled-primary btn"
        onclick={() => onApply(draftTaskIds)}
      >
        Apply
      </button>
    </footer>
  </div>
</div>
